import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { Tabs, type ITabItem, SpinnerLoader, Typography } from 'shared/ui'
import { Table } from 'shared/ui/TableNew'
import { useCreditUsageAnalyticsPageContext } from 'pages/analytics/pages/creditUsage/context/creditUsageAnalyticsPageContext'
import { type TabType } from 'pages/analytics/pages/creditUsage/store/creditUsageTableStore'
import styles from './styles.module.scss'
import { getTableColumns } from './columns'
import { getRowActions } from './rowActions'

export const CreditUsageTable = observer(() => {
  const { tableStore, onOpenContactsDetails } = useCreditUsageAnalyticsPageContext()
  const {
    setActiveTab,
    activeTab,
    tableStore: tableStoreProps,
    itemsRows,
    loading,
    firstLoading,
    getMessage,
  } = tableStore

  const tabs: ITabItem<TabType>[] = [
    { name: 'Message logs', key: 'messages' },
    { name: 'Call logs', key: 'calls' },
    { name: 'Texting agent logs', key: 'chatbots' },
    { name: 'Calling agent logs', key: 'ai_calling_agent' },
    { name: 'Transcript logs', key: 'transcripts' },
  ]

  const handleTabChange = (tab: ITabItem<TabType>) => {
    setActiveTab(tab.key)
  }

  const rows = useMemo(() => {
    return itemsRows.map((item) => {
      item.setActionsProps({
        actions: getRowActions(item, getMessage, onOpenContactsDetails),
      })
      item.setDisabled(!!item.disabled)
      return item
    })
  }, [itemsRows])

  const columns = useMemo(() => getTableColumns(activeTab), [activeTab])

  const getContent = () => {
    if (firstLoading) {
      return (
        <div className={styles.emptyState}>
          <SpinnerLoader ariaLabel={'CreditUsageTable'} />
        </div>
      )
    }

    if (!loading && !itemsRows.length) {
      return (
        <div className={styles.emptyState}>
          <Typography variant='body-rg-medium' ariaLabel='empty_placeholder' color='tertiary'>
            No {activeTab === 'chatbots' || activeTab === 'ai_calling_agent' ? 'agents' : activeTab}{' '}
            found
          </Typography>
        </div>
      )
    }

    return (
      <div className={styles.tableContainer}>
        <Table
          virtualization
          ariaLabel={'CreditUsageTable'}
          store={tableStoreProps}
          columns={columns}
          rows={rows}
          loading={loading}
        />
      </div>
    )
  }

  return (
    <div className={styles.creditUsageTable}>
      <Tabs
        tabs={tabs}
        activeTabKey={activeTab}
        handleSelectTab={handleTabChange}
        variant='default'
      />
      {getContent()}
    </div>
  )
})
